import Input from "@material-ui/core/Input";
import React from "react";
import { processValue } from "../utils";
import { WidgetProps } from "./models";

interface BaseInputState {
  value?: string | number | null;
}

interface BaseInputProps extends WidgetProps<string | number> {
  type?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  multiline?: boolean;
  options: {
    inputType?: string;
    emptyValue?: string | number;
  };
}

export const BaseInputComponent = class extends React.Component<BaseInputProps, BaseInputState> {
  constructor(props: BaseInputProps) {
    super(props);

    const { value } = this.props;

    this.state = { value };
  }

  public render() {
    const {
      id,
      schema,
      type,
      startAdornment,
      endAdornment,
      multiline,
      placeholder,
      disabled,
      autofocus,
      options,
    } = this.props;
    const { value } = this.state;

    let inputType = (options && options.inputType) || type;
    // If the input type was not explicitly defined, set an appropriate input type corresponding to the schema type...
    if (!inputType) {
      switch (schema.type) {
        case "boolean":
          inputType = "checkbox";
          break;
        case "integer":
        case "number":
        case "string":
        default:
          inputType = "text";
          break;
      }
    }

    return (
      <Input
        id={id}
        type={inputType}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        multiline={multiline}
        rowsMax={multiline ? 10 : undefined}
        value={value != null ? value : ""}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autofocus}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
        onFocus={this.handleFocus}
      />
    );
  }

  public componentDidUpdate(prevProps: BaseInputProps) {
    const { value: prevValue } = prevProps;
    const { value, options } = this.props;
    // If the value was updated outside the input (i.e. custom formData manipulation at form-level)...
    if (prevValue !== value) {
      // If the value is undefined and an empty value is specified, use it
      const nextValue: string | number | undefined | null =
        typeof value === "undefined" && typeof options.emptyValue !== "undefined" ? options.emptyValue : value;

      this.setState({ ...this.state, value: nextValue });
    }
  }

  public handleChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputElement = event.target as HTMLInputElement | HTMLTextAreaElement;

    this.setState({ value: inputElement.value });
  };

  public handleBlur = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, schema, required, options, onChange, onBlur } = this.props;

    const inputElement = event.target as HTMLInputElement | HTMLTextAreaElement;
    const value = inputElement.value;
    const processedValue =
      // Trim empty whitespace on blur
      processValue(value && value.trim(), schema.type, required, options && options.emptyValue) as
        | string
        | number
        | undefined
        | null;

    this.setState({ value: processedValue }, () => {
      if (onBlur) {
        onBlur(id, processedValue);
      }

      onChange(processedValue);
    });
  };

  public handleFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, onFocus } = this.props;

    const inputElement = event.target as HTMLInputElement | HTMLTextAreaElement;
    // No value processing required on focus
    const value = inputElement.value;

    if (onFocus) {
      onFocus(id, value);
    }
  };
};

export const BaseInput = BaseInputComponent;
